import React from "react";
import styled from "styled-components";

import { Card, CardContent } from "components/ui/Card";
import { formatNumberToWonCurrency } from "utils/format";
import type { MetricWithObject } from "types/BusinessDashboard";

import { SalesChangeIndicator } from "./sales-change-indicator";
import { HelpIcon } from "assets/icons/system";
import { BusinessDashboardHelpHoverCard } from "components/businessDashboard/ui-kit/help-modal";

const StyledCard = styled(Card)`
  border: 0;
  padding: 0;
  border-radius: 8px;
  box-shadow: none;
  background: white;
`;

const StyledCardContent = styled(CardContent)<{ $big?: boolean }>`
  padding: ${({ $big }) => ($big ? "0 8px" : "12px 16px")};
`;

const SpaceY = styled.div<{ $big?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $big }) => ($big ? "4px" : "6px")};
`;

const HelpButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  cursor: pointer;
  background: none;
  border: none;
  transition: color 0.2s;
  &:hover {
    color: #fff;
  }
`;

const Title = styled.p`
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: -0.2px;
  color: #222222;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const StatContent = styled.div<{ $big?: boolean }>`
  display: flex;
  flex-direction: ${({ $big }) => ($big ? "row" : "column")};
  gap: ${({ $big }) => ($big ? "7px" : "0")};
`;

const StatValue = styled.span<{ $big?: boolean }>`
  font-size: ${({ $big }) => ($big ? "27.99px" : "18px")};
  font-weight: bold;
  line-height: ${({ $big }) => ($big ? "34.99px" : "28px")};
  letter-spacing: ${({ $big }) => ($big ? "-0.35px" : "-0.4px")};
  color: #333333;
`;

const ChangeRow = styled.div<{ $big?: boolean; $isPositive: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 15px;
  font-weight: bold;
  line-height: 22px;
  letter-spacing: -0.2px;
`;

export const SalesStatCard = ({
  title,
  metric,
  className,
  big,
  keyName,
}: {
  title: string;
  metric: MetricWithObject;
  className?: string;
  big?: boolean;
  keyName?: string;
}) => {
  const displayValue = metric.revenue ?? metric.amount;
  const isPositive = (metric.changeRate?.value ?? 0) >= 0;

  return (
    <StyledCard className={className}>
      <StyledCardContent $big={big}>
        <SpaceY $big={big}>
          <Title>
            {title}
            {keyName && (
              <BusinessDashboardHelpHoverCard type={keyName as any}>
                <HelpButton type="button">
                  <HelpIcon />
                </HelpButton>
              </BusinessDashboardHelpHoverCard>
            )}
          </Title>
          <StatContent $big={big}>
            <StatValue $big={big}>
              {formatNumberToWonCurrency(displayValue)}
            </StatValue>
            <ChangeRow $big={big} $isPositive={isPositive}>
              <SalesChangeIndicator changeRate={metric.changeRate} />
            </ChangeRow>
          </StatContent>
        </SpaceY>
      </StyledCardContent>
    </StyledCard>
  );
};
